import {useState, useMemo, useEffect} from "react";
import moment from "moment-timezone";
import JSZip from "jszip";
import {saveAs} from "file-saver";
import {Download} from "lucide-react";
import {RecordingPlayer} from "./RecordingPlayer";

const OrbitLoader = () => (
  <div className="relative h-10 w-10 animate-spin">
    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2 h-2 bg-[#2ba9cd] rounded-full"></div>
    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-2 h-2 bg-[#2ba9cd]/50 rounded-full"></div>
    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-2 h-2 bg-[#2ba9cd]/50 rounded-full"></div>
    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 bg-[#2ba9cd]/50 rounded-full"></div>
  </div>
);

// Added activeId and setActiveId to props
const LogsView = ({logs, isLoading, activeId, setActiveId}) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [visibleCount, setVisibleCount] = useState(50);

  // States for the Zip logic
  const [isZipping, setIsZipping] = useState(false);
  const [zipProgress, setZipProgress] = useState(0);

  useEffect(() => {
    setVisibleCount(50);
    setSelectedIds([]);
  }, [logs]);

  const displayedLogs = useMemo(
    () => logs?.slice(0, visibleCount) || [],
    [logs, visibleCount],
  );

  const handleDownloadZip = async () => {
    if (selectedIds.length === 0) return;

    setIsZipping(true);
    setZipProgress(0);

    const zip = new JSZip();
    const timestamp = moment().format("YYYYMMDD_HHmmss");
    const folder = zip.folder(`recordings_export_${timestamp}`);

    const selectedLogs = logs.filter((l) => selectedIds.includes(l.ContactId));
    const totalFiles = selectedLogs.length;
    let downloadedCount = 0;

    // Process in chunks of 5 to avoid S3 503 (Throttling) errors
    const CHUNK_SIZE = 5;

    try {
      for (let i = 0; i < selectedLogs.length; i += CHUNK_SIZE) {
        const chunk = selectedLogs.slice(i, i + CHUNK_SIZE);

        await Promise.all(
          chunk.map(async (log) => {
            try {
              if (!log.recordingUrl) return;

              const res = await fetch(log.recordingUrl, {
                mode: "cors",
              });

              if (!res.ok) {
                console.error(`S3 Error ${res.status} for ${log.ContactId}`);
                return;
              }

              const blob = await res.blob();
              const cleanPhone =
                log?.customer_phonenumber?.replace(/[^\d]/g, "") || "unknown";
              const timestamp = moment
                .tz(log.start_time, "UTC")
                .format("MMDDYYYYHHmmss");
              const fileName = `${log?.client_name || "default"}_${cleanPhone}_${timestamp}.wav`;

              folder.file(fileName, blob);
            } catch (fileErr) {
              console.error(`Fetch failed for ${log.ContactId}:`, fileErr);
            } finally {
              downloadedCount++;
              setZipProgress(Math.round((downloadedCount / totalFiles) * 100));
            }
          }),
        );

        // Optional short delay between chunks to let the connection breathe
        if (i + CHUNK_SIZE < selectedLogs.length) {
          await new Promise((r) => setTimeout(r, 150));
        }
      }

      const zipContent = await zip.generateAsync({type: "blob"});
      saveAs(zipContent, `recordings_export_${timestamp}.zip`);
    } catch (err) {
      console.error("Zip generation failed:", err);
      alert(
        "Failed to generate zip. Check console for CORS or Network errors.",
      );
    } finally {
      setIsZipping(false);
      setZipProgress(0);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === displayedLogs.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(displayedLogs.map((l) => l.ContactId));
    }
  };

  return (
    <div className="p-4 space-y-4">
      {/* Header Section */}
      <div className="flex justify-between items-center bg-white p-4 rounded-xl shadow-sm border">
        <div>
          <h2 className="text-xl font-bold">Recordings Logs</h2>
          <p className="text-xs text-gray-500">
            Showing {displayedLogs?.length} of {logs?.length || 0}
          </p>
        </div>
        <div className="flex gap-2">
          {selectedIds.length > 0 && (
            <button
              onClick={handleDownloadZip}
              disabled={isZipping}
              className={`flex items-center gap-2 bg-[#2ba9cd] text-white px-4 rounded-lg text-sm py-2 transition-all ${
                isZipping ? "opacity-70 cursor-wait" : "hover:bg-[#239abf]"
              }`}
            >
              {isZipping ? (
                <>
                  <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Processing ({zipProgress}%)
                </>
              ) : (
                <>
                  <Download size={16} />
                  Download Zip ({selectedIds.length})
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Recordings table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="max-h-[55vh] overflow-auto">
          <table className="w-full text-left border-collapse">
            <thead className="bg-gray-50 sticky top-0 z-20">
              <tr>
                <th className="p-2 w-10">
                  <input
                    type="checkbox"
                    onChange={toggleSelectAll}
                    checked={
                      displayedLogs.length > 0 &&
                      selectedIds.length === displayedLogs.length
                    }
                    className="cursor-pointer"
                  />
                </th>
                <th className="p-2 text-xs font-semibold text-gray-500 uppercase">
                  Time
                </th>
                <th className="p-2 text-xs font-semibold text-gray-500 uppercase">
                  Phone
                </th>
                <th className="p-2 text-xs font-semibold text-gray-500 uppercase">
                  Name
                </th>
                <th className="p-2 text-xs font-semibold text-gray-500 uppercase">
                  Program
                </th>
                <th className="p-2 text-xs font-semibold text-gray-500 uppercase">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="p-20 text-center">
                    <div className="flex justify-center">
                      <OrbitLoader />
                    </div>
                  </td>
                </tr>
              ) : displayedLogs.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="p-20 text-center text-gray-400 italic"
                  >
                    No recordings found.
                  </td>
                </tr>
              ) : (
                displayedLogs.map((log) => (
                  <tr
                    key={log.ContactId}
                    className="border-t hover:bg-gray-50 transition-colors"
                  >
                    <td className="p-2">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(log.ContactId)}
                        onChange={() => toggleSelect(log.ContactId)}
                        className="cursor-pointer"
                      />
                    </td>
                    <td className="p-2 text-sm">
                      {moment.tz(log.start_time, "UTC").format("Do MMM hh:mm A")}
                    </td>
                    <td className="p-2 text-sm text-gray-600">
                      {log.customer_phonenumber}
                    </td>
                    <td className="p-2 text-sm text-gray-600">
                      {log.customer_firstname || log.customer_lastname
                        ? `${log.customer_firstname || ""} ${log.customer_lastname || ""}`.trim()
                        : "N/A"}
                    </td>
                    <td className="p-2 text-sm text-gray-600">
                      {log.client_name}
                    </td>
                    <td className="p-2">
                      <RecordingPlayer
                        url={log.recordingUrl}
                        id={log.ContactId}
                        activeId={activeId}
                        onPlay={setActiveId}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!isLoading && visibleCount < logs.length && (
          <div className="p-2 text-center border-t bg-gray-50">
            <button
              onClick={() => setVisibleCount((v) => v + 50)}
              className="text-sm font-normal text-[#2ba9cd] hover:text-[#239abf]"
            >
              Show More Results
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LogsView;
